import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, UtensilsCrossed, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { getProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CartDrawer } from "../components/CartDrawer";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { addItem, cartEnabled } = useCart();

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getProduct(id)
      .then(setProduct)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  const soldOut = product && product.stock <= 0;
  const gallery = product && product.images && product.images.length > 0 ? product.images : (product ? [product.image] : []);
  const [active, setActive] = useState(0);

  const handleAdd = () => {
    addItem(product);
    toast.success(`${product.name} aggiunta al carrello`);
  };

  if (loading) {
    return (
      <div className="bg-[#0D110D] min-h-screen">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center text-[#E8E2D2]/40 font-mono-accent text-xs uppercase tracking-widest">Caricamento...</div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="bg-[#0D110D] min-h-screen">
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="product-notfound">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#D47738] mb-6">Birra non trovata</p>
          <h1 className="font-serif-display text-4xl md:text-6xl text-[#E8E2D2] mb-8 leading-none">
            Questa birra <span className="italic">è finita</span>
          </h1>
          <p className="text-[#E8E2D2]/60 max-w-md mb-10">
            La birra che cerchi non è più disponibile o il link non è valido. Torna al catalogo per scoprire le altre.
          </p>
          <Link to="/" className="inline-block bg-[#D47738] text-[#0D110D] px-10 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors" data-testid="notfound-back-btn">
            Torna al Catalogo
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#0D110D] min-h-screen">
      <Navbar />
      <main className="pt-20" data-testid="product-detail-page">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#E8E2D2]/50 hover:text-[#D47738] transition-colors mb-10"
            data-testid="detail-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Torna al catalogo
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="lg:sticky lg:top-28"
              data-testid="detail-gallery"
            >
              <div className="relative bg-[#141A14] overflow-hidden">
                <motion.img
                  key={active}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  src={gallery[active]}
                  alt={`${product.name} — ${product.style} ${product.volume}, Birrificio Vecchio Cinghiale`}
                  className="w-full h-[60vh] lg:h-[78vh] object-cover"
                  data-testid="detail-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D110D]/60 to-transparent pointer-events-none" />
                {soldOut && (
                  <div className="absolute top-6 left-6 bg-[#9E3C3C] text-[#E8E2D2] font-mono-accent text-[10px] uppercase tracking-[0.2em] px-4 py-2" data-testid="detail-soldout">
                    Esaurito
                  </div>
                )}
                {gallery.length > 1 && (
                  <>
                    <button
                      onClick={() => setActive((a) => (a - 1 + gallery.length) % gallery.length)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-[#0D110D]/70 border border-white/20 text-[#E8E2D2] p-3 hover:bg-[#D47738] hover:text-[#0D110D] transition-colors"
                      data-testid="gallery-prev"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setActive((a) => (a + 1) % gallery.length)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-[#0D110D]/70 border border-white/20 text-[#E8E2D2] p-3 hover:bg-[#D47738] hover:text-[#0D110D] transition-colors"
                      data-testid="gallery-next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
              {gallery.length > 1 && (
                <div className="flex gap-3 mt-3">
                  {gallery.map((g, gi) => (
                    <button
                      key={g}
                      onClick={() => setActive(gi)}
                      className={`w-20 h-24 overflow-hidden border ${active === gi ? "border-[#D47738]" : "border-white/15"} transition-colors`}
                      data-testid={`gallery-thumb-${gi}`}
                    >
                      <img src={g} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#D47738] mb-5">
                {product.style} — {product.volume}
              </p>
              <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.9] tracking-tight text-[#E8E2D2] mb-6">
                {product.name}
              </h1>
              <p className="text-lg text-[#E8E2D2]/60 leading-relaxed mb-8 max-w-xl">
                {product.description}
              </p>

              <div className="grid grid-cols-3 border-y border-white/10 divide-x divide-white/10 mb-10">
                <Stat label="Gradazione" value={`${product.abv}%`} />
                <Stat label="Amaro (IBU)" value={product.ibu ?? "—"} />
                <Stat label="Formato" value={product.volume} />
              </div>

              <div className="flex items-center justify-between gap-6 mb-10">
                <span className="font-serif-display text-4xl text-[#D47738]" data-testid="detail-price">
                  € {product.price.toFixed(2)}
                </span>
                {cartEnabled && !soldOut && (
                  <button
                    onClick={handleAdd}
                    className="flex items-center gap-2 bg-[#D47738] text-[#0D110D] px-10 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors duration-300"
                    data-testid="detail-add-to-cart"
                  >
                    <Plus className="w-4 h-4" /> Aggiungi al carrello
                  </button>
                )}
                {cartEnabled && soldOut && (
                  <span className="border border-white/15 px-10 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#E8E2D2]/40">
                    Esaurito
                  </span>
                )}
              </div>

              {product.food_pairings && product.food_pairings.length > 0 && (
                <section className="mb-12" data-testid="detail-pairings">
                  <div className="flex items-center gap-3 mb-5">
                    <UtensilsCrossed className="w-5 h-5 text-[#D47738]" strokeWidth={1.5} />
                    <h2 className="font-serif-display text-2xl text-[#E8E2D2]">Abbinamenti consigliati</h2>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {product.food_pairings.map((f) => (
                      <span key={f} className="border border-white/15 px-4 py-2 text-sm text-[#E8E2D2]/70">
                        {f}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {product.recipe_story && (
                <section data-testid="detail-story">
                  <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#D47738] mb-4">
                    La storia della ricetta
                  </p>
                  <p className="font-serif-display text-xl md:text-2xl leading-relaxed text-[#E8E2D2]/80 italic">
                    {product.recipe_story}
                  </p>
                </section>
              )}
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="px-4 py-5 text-center">
    <p className="font-serif-display text-2xl text-[#E8E2D2]">{value}</p>
    <p className="font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#E8E2D2]/40 mt-1">{label}</p>
  </div>
);
